import * as React from 'react';
import * as RadioGroup from '@radix-ui/react-radio-group';
import { cn } from '@/lib/cn';

export interface RadioCardOption<T extends string> {
  value: T;
  label: string;
  description?: string;
  icon?: React.ReactNode;
}

interface Props<T extends string> {
  value: T;
  onValueChange: (v: T) => void;
  options: ReadonlyArray<RadioCardOption<T>>;
  columns?: 2 | 3 | 4;
  name: string;
}

export function RadioCardGroup<T extends string>(props: Props<T>): React.ReactElement {
  const cols =
    props.columns === 4 ? 'sm:grid-cols-4' : props.columns === 3 ? 'sm:grid-cols-3' : 'sm:grid-cols-2';
  return (
    <RadioGroup.Root
      value={props.value}
      onValueChange={(v) => props.onValueChange(v as T)}
      className={cn('grid grid-cols-2 gap-2', cols)}
      name={props.name}
    >
      {props.options.map((opt) => (
        <RadioGroup.Item
          key={opt.value}
          value={opt.value}
          className={cn(
            'group relative flex flex-col items-start gap-1 rounded-lg border border-input p-3 text-left',
            'hover:border-primary/50 data-[state=checked]:border-primary data-[state=checked]:bg-accent',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          )}
        >
          <div className="flex items-center gap-2">
            {opt.icon ? <span aria-hidden>{opt.icon}</span> : null}
            <span className="text-sm font-medium">{opt.label}</span>
          </div>
          {opt.description ? (
            <span className="text-xs text-muted-foreground">{opt.description}</span>
          ) : null}
          <span
            aria-hidden
            className="absolute right-2 top-2 size-2 rounded-full bg-primary opacity-0 group-data-[state=checked]:opacity-100"
          />
        </RadioGroup.Item>
      ))}
    </RadioGroup.Root>
  );
}
